import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import {
  inventorySavedViewsController,
  getInventorySavedViewsHooks,
  toSavedSnapshot,
} from '@/features/inventory/saved-views';
import { useToastStore } from '@/stores/toast-store';
import {
  isInventoryPath,
  mergeInventorySearch,
  selectInventoryTab,
  selectInventoryViewId,
} from '@/features/inventory/router-helpers';

/**
 * Bootstraps the inventory saved-view from the URL on the `/inventory` route.
 *
 * URL `?view_id=` is the source of truth. With no `view_id`, it resolves a
 * target (persisted activeViewId → server default → system view) and writes it
 * back to the URL; with a `view_id`, it loads that view's payload (skipping the
 * adapter.apply when it matches the persisted id so a plain F5 keeps the user's
 * domain edits).
 *
 * The write is gated so it can never cancel another route's transition: all
 * guard inputs come from the router store (window.location lags an SPA
 * transition), it bails while a transition is in flight, and re-runs once the
 * router goes idle. Without this, the no-view_id branch fired a competing
 * navigate({to:'/inventory', view_id}) during a pending transition and cancelled
 * navigations to /counterparties (LR-511). Mirror of the LR-208/LR-446 fix in
 * use-url-view-sync.ts.
 *
 * This hook can NOT drop an existing search param: setUrlViewId patches via
 * mergeInventorySearch(prev, …), so ?tab survives a write here. It was blamed
 * for the tab clobber twice (LR-511, LR-536) on the strength of being the last
 * writer to touch the URL; the actual dropper was AuthGuard's login bounce
 * (LR-536 — see AuthGuard.redirect-search.test.tsx).
 */
export function useInventoryViewBootstrap(): void {
  const navigate = useNavigate();
  // Needed for the async 404 catch below: render-captured router values are
  // stale by the time getDetail() rejects, so the guard there reads router.state
  // live instead.
  const router = useRouter();

  // state.location is the router's TARGET while a transition is in flight, not
  // the route being left — so the path guard rejects as soon as a navigation
  // away from /inventory starts, and isNavigating covers the window before the
  // target is known. window.location would lag both.
  const routerPath = useRouterState({ select: (s) => s.location.pathname });
  const isNavigating = useRouterState({ select: (s) => s.status === 'pending' });
  // Select primitives so the effect deps stay value-stable across re-renders.
  const tabParam = useRouterState({ select: (s) => selectInventoryTab(s) });
  const urlViewIdParam = useRouterState({ select: (s) => selectInventoryViewId(s) });

  const savedViewsHooks = getInventorySavedViewsHooks();
  const { data: savedViewsData } = savedViewsHooks.useSavedViews();
  const loadRequestIdRef = useRef(0);
  const addToast = useToastStore((s) => s.addToast);

  const setUrlViewId = useCallback(
    (viewId: number | undefined) => {
      navigate({
        to: '/inventory',
        search: (prev) => mergeInventorySearch(prev, { view_id: viewId }),
        replace: true,
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (!savedViewsData) return;
    // Never fire a competing navigate while a route transition is in flight —
    // doing so cancels the pending transition and reverts the URL to /inventory.
    // The effect re-runs once the router goes idle (status is a dep), at which
    // point routerPath is the settled route and the guards below decide
    // correctly (LR-208/LR-446 pattern from use-url-view-sync.ts).
    if (isNavigating) return;
    // Leaving /inventory: routerPath is already the target, so this rejects
    // before the component unmounts and never navigates back (LR-511).
    if (!isInventoryPath(routerPath)) return;
    // Saved views are inventory-only; leave availability / counterparty-reports
    // alone.
    if (tabParam === 'availability' || tabParam === 'counterparty-reports') return;
    const urlViewId = urlViewIdParam ?? null;
    const views = savedViewsData.results ?? [];
    const { useStore, loadView } = inventorySavedViewsController;
    const { service } = savedViewsHooks;
    const persistedId = useStore.getState().activeViewId;

    if (urlViewId == null) {
      const defaultView = savedViewsData.default_view_id
        ? views.find((v) => v.id === savedViewsData.default_view_id)
        : undefined;
      const persistedView = persistedId != null
        ? views.find((v) => v.id === persistedId)
        : undefined;
      const target = persistedView ?? defaultView ?? views.find((v) => v.is_system);
      if (target) setUrlViewId(target.id);
      return;
    }

    const { activeViewId, savedSnapshot } = useStore.getState();
    if (activeViewId === urlViewId && savedSnapshot !== null) return;

    // Name comes from the list synchronously so the selector doesn't flash "—".
    const meta = views.find((v) => v.id === urlViewId);
    if (meta) useStore.setState({ activeViewName: meta.name });

    // Same view as before reload: skip adapter.apply so user's domain edits
    // survive; just stash the snapshot for dirty-checking.
    const skipApply = persistedId === urlViewId;
    const requestId = ++loadRequestIdRef.current;
    service
      .getDetail(urlViewId)
      .then((detail) => {
        if (requestId !== loadRequestIdRef.current) return;
        if (skipApply) {
          useStore.setState({
            activeViewId: detail.id,
            activeViewName: detail.name,
            savedSnapshot: toSavedSnapshot(detail.payload),
            isDirty: false,
          });
        } else {
          loadView(detail.id, detail.name, detail.payload);
        }
      })
      .catch(() => {
        if (requestId !== loadRequestIdRef.current) return;
        // Mirror the sync guards for this async path: setUrlViewId() here would
        // clobber an in-flight or already-left route exactly like the no-view_id
        // branch did before the fix (LR-511). Read router.state LIVE — the
        // render-captured isNavigating/routerPath (and window.location, which
        // lags the router target) are all stale by the time this 404 resolves.
        if (router.state.status === 'pending' || !isInventoryPath(router.state.location.pathname)) return;
        addToast('error', 'View not found or access denied');
        setUrlViewId(undefined);
      });
    // `router` (stable useRouter() instance) is listed so exhaustive-deps is
    // satisfied for the router.state read in the catch; it never changes
    // identity, so it adds no re-runs.
  }, [savedViewsData, savedViewsHooks, tabParam, urlViewIdParam, routerPath, isNavigating, addToast, setUrlViewId, router]);
}
